/**
 * Admin — Application Themes
 *
 * Allows system admins to configure highlight / accent colors per theme.
 * Saved to localStorage and applied at runtime via ThemeProvider overrides.
 * This is the seed for the future full theme builder.
 */

import React, { useCallback, useEffect, useState } from 'react';
import styles from './AdminPages.module.css';
import {
  readThemeOverrides,
  saveThemeOverrides,
  deriveHighlightTokens,
  type ThemeOverrides,
} from '../../theme/themeOverrides';
import { useTheme } from '../../theme';

/* ── Preset swatches for quick selection ── */
const PRESETS: { label: string; hex: string }[] = [
  { label: 'Cyan',   hex: '#75E2FF' },
  { label: 'Sky',    hex: '#38BDF8' },
  { label: 'Blue',   hex: '#3B82F6' },
  { label: 'Violet', hex: '#8B5CF6' },
  { label: 'Rose',   hex: '#F43F5E' },
  { label: 'Amber',  hex: '#F59E0B' },
  { label: 'Emerald',hex: '#10B981' },
  { label: 'White',  hex: '#E2E8F0' },
];

/* ── Theme config card ── */

function ThemeCard({
  themeKey,
  label,
  bgPreview,
  textPreview,
}: {
  themeKey: string;
  label: string;
  bgPreview: string;
  textPreview: string;
}) {
  const { themeName } = useTheme();
  const isActive = themeName === themeKey;

  const allOverrides = readThemeOverrides();
  const current = allOverrides[themeKey]?.highlightColor || '';

  const [color, setColor] = useState(current);
  const [saved, setSaved] = useState(false);

  // Reset local state when overrides change externally.
  useEffect(() => {
    const all = readThemeOverrides();
    setColor(all[themeKey]?.highlightColor || '');
  }, [themeKey]);

  const isDirty = color !== current;

  const handleSave = useCallback(() => {
    if (!color) return;
    saveThemeOverrides(themeKey, { highlightColor: color });
    // Notify ThemeProvider to re-read overrides.
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }, [themeKey, color]);

  const handleReset = useCallback(() => {
    saveThemeOverrides(themeKey, { highlightColor: undefined as any });
    setColor('');
    window.dispatchEvent(new CustomEvent('l4e_theme_overrides_changed'));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }, [themeKey]);

  // Derived preview tokens
  const previewAccent = color || '#3b82f6';
  const derived = deriveHighlightTokens(previewAccent);

  return (
    <div style={{
      border: isActive ? `2px solid ${previewAccent}` : '1px solid #d1d5db',
      borderRadius: 10,
      padding: 20,
      background: '#fff',
      maxWidth: 480,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>
          {label}
        </h3>
        {isActive && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
            background: previewAccent, color: '#000',
          }}>
            ACTIVE
          </span>
        )}
      </div>

      {/* ── Live preview strip ── */}
      <div style={{
        borderRadius: 8, overflow: 'hidden', marginBottom: 16,
        border: '1px solid #e2e8f0',
      }}>
        <div style={{
          background: bgPreview, padding: '14px 16px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            background: previewAccent, flexShrink: 0,
          }} />
          <span style={{ color: textPreview, fontSize: 13, fontWeight: 600 }}>
            Selected item highlight
          </span>
          <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 6 }}>
            <div style={{
              padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: previewAccent, color: '#000',
            }}>
              Button
            </div>
            <div style={{
              padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: derived['--accent-bg'],
              border: `1px solid ${derived['--accent-border']}`,
              color: previewAccent,
            }}>
              Subtle
            </div>
          </div>
        </div>
        <div style={{
          background: bgPreview, padding: '6px 16px 10px',
          borderTop: `2px solid ${previewAccent}`,
        }}>
          <span style={{ color: previewAccent, fontSize: 12, fontWeight: 700 }}>
            Active Tab
          </span>
        </div>
      </div>

      {/* ── Color picker row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' as const }}>
          Highlight Color
        </label>
        <input
          type="color"
          value={color || '#3b82f6'}
          onChange={(e) => setColor(e.target.value)}
          style={{ width: 36, height: 30, border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', padding: 2 }}
        />
        <input
          type="text"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          placeholder="#3b82f6 (default)"
          spellCheck={false}
          style={{
            flex: 1, padding: '6px 10px', fontSize: 13, fontFamily: 'monospace',
            border: '1px solid #d1d5db', borderRadius: 6, background: '#f8fafc', color: '#1a1a2e',
          }}
        />
      </div>

      {/* ── Preset swatches ── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 16 }}>
        {PRESETS.map((p) => (
          <button
            key={p.hex}
            type="button"
            onClick={() => setColor(p.hex.toLowerCase())}
            title={`${p.label} (${p.hex})`}
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: p.hex,
              border: color?.toUpperCase() === p.hex.toUpperCase()
                ? '2px solid #1a1a2e'
                : '1px solid #d1d5db',
              cursor: 'pointer',
              transition: 'transform 100ms ease',
            }}
          />
        ))}
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={!isDirty && !!color}
          onClick={handleSave}
          style={{ opacity: isDirty || !color ? 1 : 0.5 }}
        >
          Apply
        </button>
        {color && (
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={handleReset}
          >
            Reset to Default
          </button>
        )}
        {saved && (
          <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
            Saved
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Main page ── */

export default function AdminThemes() {
  return (
    <div>
      <h1 className={styles.pageTitle}>Application Themes</h1>
      <p className={styles.pageSubtitle}>
        Configure highlight colors for each theme. Changes apply immediately for
        the active theme. The full theme builder is coming in a future release.
      </p>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' as const, marginTop: 24 }}>
        <ThemeCard
          themeKey="dark"
          label="Dark Theme"
          bgPreview="#0f172a"
          textPreview="#f8fafc"
        />
        <ThemeCard
          themeKey="light"
          label="Light Theme"
          bgPreview="#ffffff"
          textPreview="#020617"
        />
      </div>

      {/* Future: full theme builder, community themes, etc. */}
      <div style={{
        marginTop: 32, padding: '16px 20px', borderRadius: 8,
        background: '#f1f5f9', border: '1px solid #e2e8f0',
      }}>
        <p style={{ margin: 0, fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>
          Future capabilities: full theme builder with background, text, and border tokens;
          community-level theme overrides; reward / seasonal themes; theme import/export.
        </p>
      </div>
    </div>
  );
}
